import {WriteButton} from '../../common/buttons/WriteActions';
import {
    Box,
    Alert,
    Button,
    Card,
    CardContent,
    Container,
    Divider,
    FormControl,
    MenuItem,
    Select,
    Stack,
    TableCell,
    TableRow,
    Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import {DecorationGuidDto} from '../../../models/Decorations/IDecorationGuid';
import {useParams} from "react-router";
import {decorationAPI} from "../../../services/DecorationService";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import {useNavigate} from "react-router-dom";
import {ArrowBackIosNew} from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import {useEffect, useState} from "react";
import {DeleteDecorationDialog} from "./DeleteDecorationDialog";
import {AmountValue} from "../shared/AmountValue";
import {RECIPE_SIZE} from "@mybake/ui/common/dictionary";
import {useCurrency} from '../../../hooks/useCurrency';
import {getApiErrorMessage, getApiTraceId} from '../../../common/apiErrors';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {ContentSkeleton} from '../../common/loading/ContentSkeleton';

export const DecorationDetailCard = () => {
    const {formatCurrency} = useCurrency();
    const {t} = useAppTranslation();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const navigate = useNavigate();
    const {id} = useParams();
    const {data: decoration, error, isLoading, isSuccess} = decorationAPI.useGetDecorationV2Query(id || '');
    const [deleteDecoration] = decorationAPI.useDeleteDecorationV2Mutation();
    const [decorationForm, setDecorationForm] = useState<DecorationGuidDto | null>(null);
    const [deleteError, setDeleteError] = useState('');
    const [deleteTraceId, setDeleteTraceId] = useState('');
    const handleDelete = () => {
        setDeleteError('');
        setDeleteDialogOpen(!deleteDialogOpen);
    }

    const handleDeleteDecoration = async () => {
        if (!decorationForm) return false;

        try {
            await deleteDecoration(decorationForm.id ?? id ?? '').unwrap();
            navigate("..", {relative: "path"});
            return true;
        } catch (error) {
            const traceId = getApiTraceId(error);
            setDeleteError(getApiErrorMessage(error, t));
            setDeleteTraceId(traceId);
            return false;
        }
    };

    useEffect(() => {
        if (isSuccess && decoration) {
            setDecorationForm(decoration);
        }
    }, [isSuccess, decoration]);

    const [size, setSize] = useState("1.00");
    const handleRecipeSize = (e: any, counter: any) => {
        setSize(e.target.value);
    };

    return (
        <Container maxWidth="xl" sx={{py: 4,}}>
            {isLoading && <ContentSkeleton variant="details"/>}
            {error && <Alert severity="error" sx={{mb: 2}}>{getApiErrorMessage(error, t)}</Alert>}
            <Button variant="outlined" startIcon={<ArrowBackIosNew/>}
                    onClick={() => navigate("..", {relative: "path"})}>
                Back to decorations
            </Button>
            {!isLoading && <Stack spacing={1}>
                <Card>
                    <CardContent>
                        <Stack spacing={1}>
                            <Stack direction="row"
                                   spacing={2}
                                   justifyContent="space-between"
                                   alignItems="center">
                                <Box>
                                    <Typography variant="h5">
                                        {decorationForm?.name}
                                    </Typography>
                                </Box>
                                <Box paddingRight={2}>
                                    <Stack direction="row"
                                           spacing={2}
                                           justifyContent="space-between"
                                           alignItems="center">
                                        <Typography variant="subtitle1">
                                            {t('recipes:proportion')}
                                        </Typography>
                                        <FormControl
                                            variant="outlined"
                                            size="medium"
                                        >
                                            <Select
                                                labelId="recipe_size_label"
                                                id="create_recipe_select"
                                                value={size}
                                                onChange={handleRecipeSize}
                                            >
                                                {RECIPE_SIZE.map((option) => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                </Box>
                            </Stack>
                            <Divider/>

                            <Stack direction={{xs: "column", md: "row",}} spacing={1}>
                                <Box flex={1}>
                                    <Typography variant="caption">
                                        Description
                                    </Typography>

                                    <Typography variant="body1" sx={{mt: 1,}}>
                                        {decorationForm?.description || "No description"}
                                    </Typography>
                                </Box>

                                <Box minWidth={200}>
                                    <Typography variant="caption">
                                        Price
                                    </Typography>

                                    <Typography variant="h5" sx={{mt: 1,}}>
                                        {
                                            formatCurrency(Number((decorationForm?.price || 0) * Number(size)))
                                        }{" "}
                                        $
                                    </Typography>
                                </Box>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>

                {/* ITEMS */}

                <Card>
                    <CardContent>
                        <Stack spacing={1}>
                            <Typography variant="h5">
                                Items
                            </Typography>

                            <Divider/>

                            <TableContainer component={Paper} elevation={0}>
                                <Table>
                                    <TableBody>
                                        {decorationForm?.items.map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell>
                                                        {item.name}
                                                    </TableCell>

                                                    <TableCell align="right">
                                                        <AmountValue quantity={Number(item.quantity) * Number(size)} unit={item.unit}/>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Stack>
                    </CardContent>
                </Card>

                {/* ACTIONS */}

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <WriteButton variant="outlined" startIcon={<EditIcon/>} onClick={() => navigate('edit')}>
                        Edit
                    </WriteButton>

                    <WriteButton
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon/>}
                        onClick={handleDelete}
                    >
                        Delete
                    </WriteButton>
                </Stack>
            </Stack>}
            {deleteDialogOpen && <DeleteDecorationDialog open={deleteDialogOpen} onClose={handleDelete}
                                                         decorationName={decorationForm?.name || ''}
                                                         error={deleteError}
                                                         traceId={deleteTraceId}
                                                         onDelete={handleDeleteDecoration}/>}
        </Container>
    );
};
